import ClipboardJS from "clipboard";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import hljs from "highlight.js";
import "highlight.js/styles/xcode.css";
import plantumlEncoder from "plantuml-encoder";
import { useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getInternalPath, hashContent } from "utils/utils";
import "./Markdown.scss";

const PlantUMLRenderer = ({ value }: { value: string }) => {
    const encoded = plantumlEncoder.encode(value);

    // public plantuml server
    const imageUrl = `https://www.plantuml.com/plantuml/svg/${encoded}`;

    return (
        <div className="plantuml-diagram">
            <img src={imageUrl} alt="PlantUML diagram" />
        </div>
    );
};

export interface MarkdownProps {
    markdownString: string;
    className?: string;
    components?: any;
    canCopy?: boolean;
    uniqueKey?: string;
}

const copyCodeButton = "copy-code-button";
const LANGUAGE_PLANTUML = "language-plantuml";

// Stable, module-level renderer for `code` nodes. react-markdown uses the
// component referenced at `components.code` directly as the rendered element
// *type*; if that reference changes between renders (as it did when this was an
// inline function defined in the component body), React unmounts and remounts
// the whole `<code>` subtree on every render — including the unrelated
// websocket-driven re-renders — which destroys the live DOM nodes (and the
// highlight.js spans) that hold an in-progress text selection. Keeping the
// reference stable lets React reconcile the existing nodes instead of
// remounting them, so a selection survives incoming store updates.
const CodeRenderer = ({ className, children, node, ...rest }: any) => {
    const { inline, ...restProps } = rest;
    const match = new RegExp(LANGUAGE_PLANTUML).exec(className || "");

    if (match && !inline) {
        const value = String(children).replace(/\n$/, "");
        return <PlantUMLRenderer value={value} />;
    }

    if (!inline) {
        return (
            <code
                {...restProps}
                className={className}
                ref={(el) => {
                    if (el && !el.dataset.highlighted) {
                        hljs.highlightElement(el);
                    }
                }}
            >
                {children}
            </code>
        );
    }
    return (
        <code {...restProps} className={className}>
            {children}
        </code>
    );
};

// `pre` needs the `canCopy` prop, so it can't be a bare module constant; this
// factory produces a renderer whose identity is stable for a given `canCopy`
// (memoized per-instance below). Same rationale as CodeRenderer: a stable
// element type across renders prevents the `<pre>` subtree from remounting and
// clearing the user's text selection on unrelated store updates.
const makePreRenderer = (canCopy: boolean) => {
    const PreRenderer = ({ children, node, ...rest }: any) => {
        const childrenContentToHash = JSON.stringify(node.children);
        let elementUniqueKey = "";
        const childrenProps = (children[0] as any)?.props;

        if (
            childrenProps?.node?.tagName === "code" &&
            childrenProps?.className !== LANGUAGE_PLANTUML
        ) {
            elementUniqueKey = hashContent(childrenContentToHash);
        }
        return (
            <div
                className={`codeBlock ${
                    canCopy && elementUniqueKey ? "" : "copyButtonHidden"
                }`}
            >
                <pre
                    ref={(el) => {
                        const firstChild = el?.firstElementChild;
                        if (elementUniqueKey) {
                            new ClipboardJS(
                                `${
                                    elementUniqueKey
                                        ? `[data-unique="${elementUniqueKey}"] .${copyCodeButton}`
                                        : `.${copyCodeButton}`
                                }`,
                                {
                                    target: () => {
                                        return firstChild as Element;
                                    },
                                }
                            );
                        }
                    }}
                    {...rest}
                >
                    {children}
                </pre>
                <div className="copyButtonContainer" data-unique={elementUniqueKey}>
                    <Tooltip overlay="Copy code">
                        <Button
                            size="small"
                            icon={IconCollection.contentCopy}
                            variation="clear"
                            className={copyCodeButton}
                        />
                    </Tooltip>
                </div>
            </div>
        );
    };
    return PreRenderer;
};

export const Markdown = ({
    markdownString,
    className,
    components,
    canCopy = true,
    uniqueKey,
}: MarkdownProps) => {
    const markdownContainerRef = useRef<any>();

    // Stable renderer references (see CodeRenderer / makePreRenderer above).
    // `code` is prop-independent so it is a module constant; `pre` depends on
    // `canCopy`, so it is memoized per instance and only changes when `canCopy`
    // does. The merged object being rebuilt each render is harmless — what
    // matters for avoiding a remount is that each individual renderer reference
    // (code/pre and any passed-in `components.*`) stays stable.
    const preRenderer = useMemo(() => makePreRenderer(canCopy), [canCopy]);
    const mergedComponents = useMemo(
        () => ({
            ...components,
            code: CodeRenderer,
            pre: preRenderer,
        }),
        [components, preRenderer]
    );

    const markdown = (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className={`markdown ${className ?? ""}`}
            linkTarget="_blank"
            components={mergedComponents}
        >
            {markdownString}
        </ReactMarkdown>
    );

    return (
        <div ref={markdownContainerRef} key={uniqueKey}>
            {markdown}
        </div>
    );
};

const LinkRenderer = (props: any) => {
    const internalPath = getInternalPath(props.href);
    const target = internalPath?.length ? "" : "_blank";

    return (
        <a href={internalPath?.length === 0 ? null : props.href} target={target} rel="noreferrer">
            {props.children}
            {internalPath === null && <Icon icon={IconCollection.openInNew} />}
        </a>
    );
};

export const CustomLinkMarkdown = ({ markdownString, className, uniqueKey }: MarkdownProps) => {
    return (
        <Markdown
            markdownString={markdownString}
            className={className}
            components={{ a: LinkRenderer }}
            uniqueKey={uniqueKey}
        />
    );
};
